import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import styles from './Gynaec.module.css';
import medicalVideo from '../../../images/gynaec.mp4';

const DrArpanaProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Content Strategy & Medical Service Mapping', icon: '💡' },
    { text: 'User Experience Content Structuring', icon: '🎯' },
    { text: 'Clinical Narrative Integration', icon: '⚙️' },
    { text: 'Website Content Deployment', icon: '🌐' },
    { text: 'Patient Assurance Messaging', icon: '📊' },
    { text: 'SEO & Engagement Optimization', icon: '📈' },
    { text: 'Maintenance & Iteration', icon: '🚀' }
  ];

  const tools = [
    { name: 'HTML/CMS Planning', icon: '📝', description: 'Content Hierarchy' },
    { name: 'Figma', icon: '📊', description: 'UX Tools' },
    { name: 'Medical Copy', icon: '🔤', description: 'Frameworks' },
    { name: 'Photoshop', icon: '🖼️', description: 'Visual Assets' },
    { name: 'Analytics', icon: '📈', description: 'Engagement Insights' }
  ];

  const skills = [
    'HTML5', 'Responsive Content', 'UX Writing', 'Healthcare Copy', 
    'Content Architecture', 'SEO Optimization', 'Medical Narrative', 
    'Patient Flow Design', 'Information Hierarchy'
  ];

  const services = [
    { 
      name: 'Obstetrics & Childbirth Care',
      description: 'Prenatal care, VBAC, C-Section options'
    },
    { 
      name: 'Gynecology',
      description: 'Laparoscopy, hysteroscopy, reproductive health'
    },
    { 
      name: 'Infertility & Reproductive Support',
      description: 'Evaluation, IUI, laparoscopic interventions'
    },
    { 
      name: 'High-Risk Pregnancy Management',
      description: 'Tailored clinical consultations'
    }
  ];

  const whyChoose = [
    'Experienced Senior Consultant',
    'Compassionate Patient Communication',
    'Personalized Care Paths',
    'Multi-location Consultations',
    'Flexible Appointment Slots'
  ];

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
        {/* Animated Background */}
        <div className={styles.backgroundGradient}>
          <div className={styles.gradientOrb1}></div>
          <div className={styles.gradientOrb2}></div>
          <div className={styles.gradientOrb3}></div>
        </div>

        <div className={styles.contentWrapper}>
          {/* Hero Section */}
          <section className={styles.heroSection}>
            <div className={styles.heroContent}>
              <div className={styles.heroLeft}>
                <div className={styles.projectBadge}>
                  <span className={styles.badgeDot}></span>
                  <span>Ongoing Project</span>
                </div>
                
                <h1 className={styles.projectTitle}>Healthcare Service Platform</h1>
                <p className={styles.projectSubtitle}>Obstetrics & Gynecology Expert</p>
                <p className={styles.projectTagline}>Comprehensive Women's Health & Clinical Care</p>
                
                <div className={styles.projectMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Work</span>
                    <span className={styles.metaValue}>Website Content Strategy, UX Content Structuring</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Year</span>
                    <span className={styles.metaValue}>2025</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Duration</span>
                    <span className={styles.metaValue}>Ongoing</span>
                  </div>
                </div>
                
                <p className={styles.heroDescription}>
                  This platform offers specialized obstetrics, gynecology, infertility, and women's health services in New Delhi. The platform delivers evidence-based clinical care, personalized treatment plans, and advanced surgical solutions — blending compassionate patient interaction with medical excellence.
                </p>
                
               
              </div>
              
              <div className={styles.heroRight}>
                <div className={styles.heroVisual}>
                  <video 
                    className={styles.heroVideo}
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                  >
                    <source src={medicalVideo} type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Introduction</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                A Healthcare Service Platform designed to deliver comprehensive and patient-centered care across obstetrics, gynecology, and fertility services. Backed by extensive clinical experience, the platform presents a wide range of treatments including high-risk pregnancy management, gynecological procedures, and infertility care. It emphasizes clear communication, personalized treatment pathways, and a compassionate approach, enabling patients to make informed healthcare decisions with confidence.
                </p>
            </div>
          </section>

          {/* My Role Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>My Role</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                My role focused on shaping the platform’s content architecture and patient-centric digital experience. This included translating complex medical services into clear, accessible web content, structuring service categories for improved usability and SEO, and highlighting clinical expertise, qualifications, and treatment benefits. I ensured that the overall messaging aligned with trust, care, and positive patient outcomes while maintaining clarity across all digital touchpoints.
              </p>
            </div>
          </section>

          {/* Responsibilities Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Responsibilities</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.responsibilitiesGrid}>
              {responsibilities.map((responsibility, index) => (
                <div 
                  key={index}
                  className={styles.responsibilityCard}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setHoveredResponsibility(index)}
                  onMouseLeave={() => setHoveredResponsibility(null)}
                >
                  <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                  <div className={styles.cardIcon}>{responsibility.icon}</div>
                  <div className={styles.cardText}>{responsibility.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Tools Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Tools Used</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.toolsGrid}>
              {tools.map((tool, index) => (
                <div 
                  key={index}
                  className={styles.toolCard}
                  style={{ animationDelay: `${index * 0.15}s` }}
                  onMouseEnter={() => setHoveredTool(index)}
                  onMouseLeave={() => setHoveredTool(null)}
                >
                  <div className={styles.toolGlow}></div>
                  <div className={styles.toolIcon}>{tool.icon}</div>
                  <div className={styles.toolName}>{tool.name}</div>
                  <div className={styles.toolDescription}>{tool.description}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Skills Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.skillsContainer}>
              {skills.map((skill, index) => (
                <div 
                  key={index}
                  className={styles.skillTag}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <span className={styles.skillHash}>#</span>
                  {skill}
                </div>
              ))}
            </div>
          </section>

          
        </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
};

export default DrArpanaProjectShowcase;